import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Building2, Check, Loader2, Package } from "lucide-react";

const INDUSTRIES = [
  "Advanced Manufacturing", "Agriculture", "Construction", "Education", "Energy",
  "Financial Services", "Healthcare", "Hospitality", "Technology", "Telecommunications", "Transportation",
];

export default function PostJob() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(0);
  const [useCredit, setUseCredit] = useState(false);
  const [form, setForm] = useState({
    title: "", occupation: "", industry: "Construction", employer: "",
    contact_email: "", city: "", state: "", wage_min: 20, wage_max: 35,
    description: "", apply_url: "", requirements: "", benefits: "", skills: "",
  });

  useEffect(() => {
    if (!user) { setCredits(0); return; }
    api.get("/employer/my-jobs").then((r) => setCredits(r.data.credits || 0)).catch(() => {});
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        location: `${form.city}, ${form.state}`,
        wage_min: Number(form.wage_min),
        wage_max: Number(form.wage_max),
        requirements: form.requirements.split("\n").filter(Boolean),
        benefits: form.benefits.split("\n").filter(Boolean),
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (useCredit && credits > 0) {
        const res = await api.post("/employer/publish-with-credit", payload);
        toast.success("Job published!");
        navigate(`/jobs/${res.data.job_id}`);
        return;
      }
      const draft = await api.post("/employer/draft", payload);
      const checkout = await api.post("/payments/checkout", {
        lookup_key: "featured_job_post",
        origin_url: window.location.origin,
        draft_id: draft.data.draft_id,
      });
      window.location.href = checkout.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not proceed");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest mb-2">For Employers</div>
      <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">Post an <span className="text-yellow-500">Apprenticeship</span></h1>
      <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed">
        Reach thousands of career-ready apprentices. Your job goes live instantly after payment. Featured for 30 days.
      </p>

      <div className="mt-8 border border-yellow-500/40 bg-yellow-500/5 p-6">
        <div className="flex items-start gap-4">
          <Building2 size={24} className="text-yellow-500 shrink-0" />
          <div className="flex-1">
            <div className="font-display text-2xl uppercase leading-none">Featured Listing &mdash; <span className="text-yellow-500">$49</span></div>
            <ul className="mt-3 text-sm text-zinc-300 space-y-1">
              <li className="flex gap-2"><Check size={14} className="text-yellow-500 mt-0.5" /> Live for 30 days</li>
              <li className="flex gap-2"><Check size={14} className="text-yellow-500 mt-0.5" /> Featured tag on job cards</li>
              <li className="flex gap-2"><Check size={14} className="text-yellow-500 mt-0.5" /> Included in AI Match pool</li>
            </ul>
          </div>
        </div>
        {user && credits > 0 && (
          <label className="mt-4 flex items-center gap-2 cursor-pointer text-sm text-yellow-500 border-t border-yellow-500/20 pt-4">
            <input
              data-testid="post-use-credit"
              type="checkbox"
              checked={useCredit}
              onChange={(e) => setUseCredit(e.target.checked)}
              className="accent-yellow-500"
            />
            <Package size={14} />
            Use 1 of {credits} bundle credit{credits !== 1 ? "s" : ""} (skip checkout)
          </label>
        )}
      </div>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label="Job Title" required>
          <input data-testid="post-title" required value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Electrical Apprentice" className={inputCls} />
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Occupation" required>
            <input data-testid="post-occupation" required value={form.occupation} onChange={(e) => set("occupation", e.target.value)} placeholder="Electrician" className={inputCls} />
          </Field>
          <Field label="Industry" required>
            <select data-testid="post-industry" value={form.industry} onChange={(e) => set("industry", e.target.value)} className={inputCls}>
              {INDUSTRIES.map((i) => (<option key={i} value={i} className="bg-zinc-900">{i}</option>))}
            </select>
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Employer / Company" required>
            <input data-testid="post-employer" required value={form.employer} onChange={(e) => set("employer", e.target.value)} placeholder="ACME Trades" className={inputCls} />
          </Field>
          <Field label="Contact Email" required>
            <input data-testid="post-email" type="email" required value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="hiring@acme.com" className={inputCls} />
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="City" required>
            <input data-testid="post-city" required value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Austin" className={inputCls} />
          </Field>
          <Field label="State" required>
            <input data-testid="post-state" required maxLength={2} value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase())} placeholder="TX" className={inputCls} />
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Min Wage ($/hr)" required>
            <input data-testid="post-wage-min" type="number" min="0" step="0.5" required value={form.wage_min} onChange={(e) => set("wage_min", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Max Wage ($/hr)" required>
            <input data-testid="post-wage-max" type="number" min="0" step="0.5" required value={form.wage_max} onChange={(e) => set("wage_max", e.target.value)} className={inputCls} />
          </Field>
        </div>
        <Field label="Description" required>
          <textarea data-testid="post-description" required rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe the apprenticeship — what it involves, hours, training…" className={`${inputCls} resize-none`} />
        </Field>
        <Field label="Apply URL" required>
          <input data-testid="post-apply-url" type="url" required value={form.apply_url} onChange={(e) => set("apply_url", e.target.value)} placeholder="https://your-company.com/careers/apply" className={inputCls} />
        </Field>
        <Field label="Requirements (one per line)">
          <textarea data-testid="post-requirements" rows={3} value={form.requirements} onChange={(e) => set("requirements", e.target.value)} placeholder={"18+\nHigh school diploma or GED\nDriver's license"} className={`${inputCls} resize-none font-mono-alt text-sm`} />
        </Field>
        <Field label="Benefits (one per line)">
          <textarea data-testid="post-benefits" rows={3} value={form.benefits} onChange={(e) => set("benefits", e.target.value)} placeholder={"Health insurance\nPension\nTuition reimbursement"} className={`${inputCls} resize-none font-mono-alt text-sm`} />
        </Field>
        <Field label="Skills (comma separated)">
          <input data-testid="post-skills" value={form.skills} onChange={(e) => set("skills", e.target.value)} placeholder="Wiring, Blueprint reading, Safety" className={inputCls} />
        </Field>

        <button
          data-testid="post-submit"
          disabled={loading}
          className="w-full bg-yellow-500 text-black font-bold uppercase tracking-wider py-4 hover:bg-yellow-400 active:scale-[0.99] transition flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? <><Loader2 className="animate-spin" size={16} /> Working&hellip;</> : (useCredit ? "Publish Instantly" : "Continue to Payment — $49")}
        </button>
        {!useCredit && <p className="text-xs text-zinc-600 text-center">Test mode. Use card 4242 4242 4242 4242, any future date, any CVC.</p>}
      </form>
    </div>
  );
}

const inputCls = "w-full bg-zinc-950 border border-zinc-800 focus:border-yellow-500 px-3 py-2.5 text-white placeholder:text-zinc-600 focus:outline-none transition";
function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-zinc-400 mb-1">{label}{required && <span className="text-yellow-500"> *</span>}</span>
      {children}
    </label>
  );
}
